/**
 * Shared date/datetime validation — year must be exactly 4 digits.
 */
import { isValidAppDate } from './appDateTime';
export const DATE_MIN = '1900-01-01';
export const DATE_MAX = '2099-12-31';
export const DATETIME_MIN = '1900-01-01T00:00';
export const DATETIME_MAX = '2099-12-31T23:59';
export const MONTH_MIN = '1900-01';
export const MONTH_MAX = '2099-12';

export const DATE_VALIDATION_MESSAGE = 'Enter a valid date with a 4-digit year (YYYY-MM-DD).';
export const DATETIME_VALIDATION_MESSAGE = 'Enter a valid date and time with a 4-digit year.';
export const MONTH_VALIDATION_MESSAGE = 'Enter a valid month with a 4-digit year (YYYY-MM).';

const DATE_PARTIAL = /^\d{0,4}(-\d{0,2}(-\d{0,2})?)?$/;
const DATETIME_PARTIAL = /^\d{0,4}(-\d{0,2}(-\d{0,2}(T\d{0,2}(:\d{0,2})?)?)?)?$/;
const MONTH_PARTIAL = /^\d{0,4}(-\d{0,2})?$/;

const DATE_FULL = /^(\d{4})-(\d{2})-(\d{2})$/;
const DATETIME_FULL = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;
const MONTH_FULL = /^(\d{4})-(\d{2})$/;

function yearOutOfRange(yearStr) {
  const year = parseInt(yearStr, 10);
  return year < 1900 || year > 2099;
}

function yearSegmentTooLong(value) {
  const year = String(value ?? '').split('-')[0];
  return year.length > 4;
}

export function sanitizeDateValue(value) {
  const v = String(value ?? '');
  if (!v) return '';
  if (yearSegmentTooLong(v) || !DATE_PARTIAL.test(v)) return null;
  return v;
}

export function sanitizeDateTimeValue(value) {
  const v = String(value ?? '');
  if (!v) return '';
  if (yearSegmentTooLong(v) || !DATETIME_PARTIAL.test(v)) return null;
  return v;
}

export function sanitizeMonthValue(value) {
  const v = String(value ?? '');
  if (!v) return '';
  if (yearSegmentTooLong(v) || !MONTH_PARTIAL.test(v)) return null;
  return v;
}

export function isValidDate(value, { required = false } = {}) {
  const v = String(value ?? '').trim();
  if (!v) return !required;
  const m = v.match(DATE_FULL);
  if (!m || m[1].length !== 4 || yearOutOfRange(m[1])) return false;
  const d = new Date(`${v}T12:00:00`);
  return isValidAppDate(v) && !Number.isNaN(d.getTime());
}

export function isValidDateTime(value, { required = false } = {}) {
  const v = String(value ?? '').trim();
  if (!v) return !required;
  const m = v.match(DATETIME_FULL);
  if (!m || m[1].length !== 4 || yearOutOfRange(m[1])) return false;
  const d = new Date(v);
  return !Number.isNaN(d.getTime());
}

export function isValidMonth(value, { required = false } = {}) {
  const v = String(value ?? '').trim();
  if (!v) return !required;
  const m = v.match(MONTH_FULL);
  if (!m || m[1].length !== 4 || yearOutOfRange(m[1])) return false;
  const month = parseInt(m[2], 10);
  return month >= 1 && month <= 12;
}

export function validateDate(value, { required = false } = {}) {
  const v = String(value ?? '').trim();
  if (!v) return required ? DATE_VALIDATION_MESSAGE : null;
  if (!isValidDate(v)) return DATE_VALIDATION_MESSAGE;
  return null;
}

export function validateDateTime(value, { required = false } = {}) {
  const v = String(value ?? '').trim();
  if (!v) return required ? DATETIME_VALIDATION_MESSAGE : null;
  if (!isValidDateTime(v)) return DATETIME_VALIDATION_MESSAGE;
  return null;
}

export function validateMonth(value, { required = false } = {}) {
  const v = String(value ?? '').trim();
  if (!v) return required ? MONTH_VALIDATION_MESSAGE : null;
  if (!isValidMonth(v)) return MONTH_VALIDATION_MESSAGE;
  return null;
}
